/**
 * WeaveIn.ai Platform - Contact List Page
 * Main view for managing contacts / pre-members with filters, pagination, and status chips
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Plus, Filter, UserPlus, ChevronLeft, ChevronRight,
  AlertTriangle, Phone, Mail, MoreHorizontal, Users, Eye, Edit, Trash2
} from 'lucide-react';
import useContacts from '@/hooks/useContacts';
import { programsAPI } from '@/services/api/programs';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface ContactListPageProps {
  onViewContact?: (contactId: number) => void;
  onEditContact?: (contactId: number) => void;
  onCreateContact?: () => void;
  /** Optional: navigate to the field-config screen (tab id "member-fields").
   *  When invoked from this page, the scope selector inits to "contact". */
  onConfigureFields?: () => void;
}

const INTAKE_SOURCE_LABELS: Record<string, string> = {
  interest_form: 'Interest Form',
  qr_code: 'QR Code Signup',
  qr_survey: 'QR Survey',
  referral_list: 'Referral List',
  ai_sourced: 'AI Sourced',
  receptionist: 'Receptionist',
  mif: 'MIF',
  file_upload: 'File Upload',
  digital_form: 'Digital Form',
  marketing: 'Marketing',
  manual: 'Manual Entry',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  outreach: 'bg-indigo-100 text-indigo-700',
  qualified: 'bg-amber-100 text-amber-700',
  eligible: 'bg-emerald-100 text-emerald-700',
  enrolled: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  ineligible: 'bg-gray-100 text-gray-600',
  unreachable: 'bg-gray-100 text-gray-500',
};

export default function ContactListPage({ onViewContact, onEditContact, onCreateContact, onConfigureFields }: ContactListPageProps) {
  const { t } = useAppTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [programFilter, setProgramFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [sourceFilter, setSourceFilter] = useState<string | undefined>();
  const [programs, setPrograms] = useState<Array<{ id: number; name: string }>>([]);

  const {
    contacts, total, totalPages, page, loading, error,
    setPage, setSearch, setFilters, refetch, archiveContact,
  } = useContacts({ autoFetch: true });

  // Load programs for filter dropdown
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const data = await programsAPI.listPrograms();
        const list = Array.isArray(data) ? data : (data as any)?.programs || [];
        setPrograms(list.map((p: any) => ({ id: p.id, name: p.name })));
      } catch {
        // Programs list unavailable — filter will just be empty
      }
    };
    loadPrograms();
  }, []);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const applyFilters = () => {
    setFilters({
      program_id: programFilter,
      status: statusFilter,
      intake_source: sourceFilter,
    });
  };

  return (
    <div className="space-y-4 p-3 sm:p-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 flex-shrink truncate text-[13px] text-ink-500">
          {t('contacts.listSubtitle', 'Pre-member contacts and leads —')} {total} {t('contacts.total', 'total')}
        </p>
        <div className="flex gap-2">
          {onConfigureFields && (
            <button
              onClick={() => {
                try {
                  window.localStorage.setItem('field_config_initial_scope', 'contact');
                } catch { /* private mode */ }
                onConfigureFields();
              }}
              className="hidden sm:flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              title={t('contacts.configureFieldsTooltip', 'Configure which fields appear when working with contacts')}
            >
              {t('contacts.configureFields', 'Configure contact fields')}
            </button>
          )}
          {onCreateContact && (
            <button
              onClick={onCreateContact}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 sm:px-4"
            >
              <UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">{t('contacts.newContact', 'New Contact')}</span><span className="sm:hidden">{t('contacts.new', 'New')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Search + Filters — selects go full-width on phone */}
      <div className="flex flex-wrap items-stretch gap-2 sm:items-end sm:gap-3">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px]">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('contacts.searchPlaceholder', 'Search by name, phone, or email...')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pl-9 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
        </div>
        <select
          value={programFilter ?? ''}
          onChange={(e) => setProgramFilter(e.target.value ? Number(e.target.value) : undefined)}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:flex-none"
        >
          <option value="">{t('contacts.allPrograms', 'All Programs')}</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => setStatusFilter(e.target.value || undefined)}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:flex-none"
        >
          <option value="">{t('contacts.allStatuses', 'All Statuses')}</option>
          <option value="new">{t('contacts.statusNew', 'New')}</option>
          <option value="outreach">{t('contacts.statusOutreach', 'Outreach')}</option>
          <option value="qualified">{t('contacts.statusQualified', 'Qualified')}</option>
          <option value="eligible">{t('contacts.statusEligible', 'Eligible')}</option>
          <option value="enrolled">{t('contacts.statusEnrolled', 'Enrolled')}</option>
          <option value="declined">{t('contacts.statusDeclined', 'Declined')}</option>
          <option value="ineligible">{t('contacts.statusIneligible', 'Ineligible')}</option>
          <option value="unreachable">{t('contacts.statusUnreachable', 'Unreachable')}</option>
        </select>
        <select
          value={sourceFilter ?? ''}
          onChange={(e) => setSourceFilter(e.target.value || undefined)}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:flex-none"
        >
          <option value="">{t('contacts.allSources', 'All Sources')}</option>
          {Object.entries(INTAKE_SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button
          onClick={applyFilters}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 dark:bg-gray-600"
        >
          <Filter className="h-4 w-4" /> <span className="hidden sm:inline">{t('contacts.apply', 'Apply')}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg">
          {error}
        </div>
      )}

      {/* Table — horizontal scroll on phone so columns stay readable */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colName', 'Name')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colContactInfo', 'Contact Info')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.source', 'Source')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colSubmittedBy', 'Submitted By')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colProgramStatus', 'Program Status')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colQuality', 'Quality')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colDuplicates', 'Duplicates')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{t('contacts.colCreated', 'Created')}</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-300">{t('contacts.colActions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && contacts.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">{t('contacts.loadingContacts', 'Loading contacts...')}</td></tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  {t('contacts.noContactsFound', 'No contacts found')}
                </td>
              </tr>
            ) : (
              contacts.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                  onClick={() => onViewContact?.(c.id)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {c.first_name} {c.last_name || ''}
                    </div>
                    <div className="text-xs text-gray-400">{c.member_number}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5 text-gray-600 dark:text-gray-300">
                      {c.phone_no && <span className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3" /> {c.phone_no}</span>}
                      {c.email && <span className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /> {c.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500">
                      {c.intake_source ? (INTAKE_SOURCE_LABELS[c.intake_source] || c.intake_source) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(c.submitted_by_name || c.submitted_by_email || c.submitted_by_phone) ? (
                      <div className="flex flex-col gap-0.5 text-gray-600 dark:text-gray-300">
                        {c.submitted_by_name && <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{c.submitted_by_name}</span>}
                        {c.submitted_by_email && <span className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /> {c.submitted_by_email}</span>}
                        {c.submitted_by_phone && <span className="flex items-center gap-1 text-xs"><Phone className="w-3 h-3" /> {c.submitted_by_phone}</span>}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.program_statuses && Array.isArray(c.program_statuses) ? (
                        c.program_statuses.map((ps: any, i: number) => (
                          <span
                            key={i}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ps.status] || 'bg-gray-100 text-gray-600'}`}
                          >
                            {ps.status}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {c.data_quality_score != null ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              c.data_quality_score >= 70 ? 'bg-green-500' : c.data_quality_score >= 40 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${c.data_quality_score}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{c.data_quality_score}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(c.pending_duplicates ?? 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        <AlertTriangle className="w-3 h-3" /> {c.pending_duplicates}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="relative" ref={openMenuId === c.id ? menuRef : undefined}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === c.id ? null : c.id)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                      {openMenuId === c.id && (
                        <div className="absolute right-0 top-8 z-20 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
                          <button
                            onClick={() => { setOpenMenuId(null); onViewContact?.(c.id); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Eye className="w-3.5 h-3.5" /> {t('buttons.view', 'View')}
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); onEditContact?.(c.id); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Edit className="w-3.5 h-3.5" /> {t('common.edit', 'Edit')}
                          </button>
                          <button
                            onClick={async () => {
                              if (confirm(`${t('contacts.archiveConfirmPrefix', 'Archive')} ${`${c.first_name} ${c.last_name || ''}`.trim()}?`)) {
                                setOpenMenuId(null);
                                try { await archiveContact(c.id); } catch {}
                              }
                            }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> {t('contacts.archive', 'Archive')}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {t('contacts.page', 'Page')} {page} {t('contacts.of', 'of')} {totalPages} ({total} {t('contacts.contactsLower', 'contacts')})
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
